import TrackedLink from "@/components/TrackedLink";
import { Button } from "@/components/ui/button";
import { Shield, ShieldAlert, ShieldCheck, Smartphone, Eye, EyeOff, UserX, Lock, Unlock, CheckCircle2, XCircle, Lightbulb } from "lucide-react";
import { motion } from "framer-motion";
import byodHeroBg from "@/assets/byod-hero-bg.jpg";

const scenarios = [
  {
    icon: Smartphone,
    title: "Outlook op een privételefoon",
    description: "Zonder pincode, zonder beveiliging. Eén verloren telefoon = toegang tot al je bedrijfsmail.",
  },
  {
    icon: Eye,
    title: "WhatsApp-groepen met klantgegevens",
    description: "Handig, maar die gegevens staan nu op het privétoestel van elke medewerker. En in hun backup.",
  },
  {
    icon: UserX,
    title: "Medewerker vertrekt",
    description: "De laptop lever je in, maar de telefoon? Die is van de medewerker zelf. Met al je bedrijfsdata erop.",
  },
  {
    icon: Unlock,
    title: "Bedrijfsdata op een gedeelde iPad",
    description: "De iPad in de vergaderruimte waar iedereen op inlogt. Handig, maar wie heeft er eigenlijk allemaal toegang?",
  },
];

const comparison = {
  without: [
    "Bedrijfsdata op onbeveiligde privétoestellen",
    "Geen controle bij vertrek van medewerkers",
    "AVG-risico bij verlies of diefstal",
    "Copy-paste van bedrijfsdata naar privéapps",
    "Geen zicht op wie waar toegang toe heeft",
  ],
  with: [
    "Zakelijke apps beveiligd, privédata onaangetast",
    "Op afstand bedrijfsdata wissen, telefoon blijft intact",
    "AVG-compliant zonder gedoe",
    "Bedrijfsdata kan niet naar privéapps gekopieerd worden",
    "Volledige controle zonder het apparaat over te nemen",
  ],
};

const voordelen = [
  {
    icon: Shield,
    title: "Geen MDM-gedoe",
    description: "Je hoeft niet het hele apparaat in te schrijven. Alleen de zakelijke apps worden beveiligd. De medewerker merkt er bijna niks van.",
  },
  {
    icon: EyeOff,
    title: "Privacy van medewerkers gewaarborgd",
    description: "Wij zien geen privéfoto's, geen persoonlijke apps, geen locatie. Alleen de zakelijke apps worden beheerd.",
  },
  {
    icon: Lock,
    title: "AVG-compliant uit de doos",
    description: "Bedrijfsdata kan niet gekopieerd worden naar privéapps. Bij verlies of vertrek wis je op afstand alleen de zakelijke data.",
  },
  {
    icon: CheckCircle2,
    title: "Medewerkers zijn blij",
    description: "Geen tweede telefoon nodig. Geen 'big brother'-gevoel. Ze gebruiken gewoon hun eigen toestel — maar dan veilig.",
  },
  {
    icon: Lightbulb,
    title: "Binnen een dag geregeld",
    description: "Wij zetten het voor je klaar. Medewerkers krijgen een simpele uitnodiging en zijn in 5 minuten aan de slag.",
  },
  {
    icon: ShieldCheck,
    title: "Werkt op iOS én Android",
    description: "Maakt niet uit welk toestel je medewerkers gebruiken. De beveiliging werkt overal hetzelfde.",
  },
];

const PraktijkBYOD = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden bg-black text-primary-foreground">
        <img loading="eager" decoding="async" fetchPriority="high" src={byodHeroBg} alt="Medewerker gebruikt eigen telefoon voor werk" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="container relative z-10 py-20 md:py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm"
            >
              <Smartphone className="h-7 w-7 text-primary" />
            </motion.div>
            <span className="mb-4 inline-block rounded-full bg-primary/20 px-4 py-1.5 text-sm font-semibold text-primary">
              Praktijkvoorbeeld
            </span>
            <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
              Je medewerkers gebruiken hun eigen telefoon.{" "}
              <span className="text-primary">Is dat veilig?</span>
            </h1>
            <p className="mb-8 text-lg text-primary-foreground/80 md:text-xl">
              De meeste bedrijven hebben een blinde vlek: medewerkers openen dagelijks zakelijke mail,
              Teams en documenten op hun privételefoon. Zonder enige beveiliging. Dit is hoe je dat oplost
              — zonder de telefoon over te nemen.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {[
                "Privétoestel blijft privé",
                "AVG-compliant",
                "Binnen een dag geregeld",
                "iOS & Android",
              ].map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-2 text-sm text-primary-foreground/90"
                >
                  <CheckCircle2 className="h-4 w-4 text-rinqel-green" />
                  <span>{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Herkenning */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Herken je dit?
            </h2>
            <p className="mb-12 text-lg text-muted-foreground">
              Dit zijn situaties die bij vrijwel elk bedrijf voorkomen. Vaak zonder dat iemand er bij stilstaat.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {scenarios.map((scenario) => (
              <div
                key={scenario.title}
                className="group rounded-2xl border border-border bg-card p-6 transition-all hover:border-destructive/30 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                  <scenario.icon className="h-6 w-6 text-destructive" />
                </div>
                <h3 className="mb-2 text-lg font-bold">{scenario.title}</h3>
                <p className="text-muted-foreground">{scenario.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Het probleem */}
      <section className="border-y border-border bg-muted/30 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">
              Waarom is dit een probleem?
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Als een medewerker zijn telefoon verliest, kan iedereen bij je bedrijfsmail. Als iemand vertrekt,
                blijft al je data op dat privétoestel staan. En als je klantgegevens rondslingeren op onbeveiligde
                apparaten? Dan heb je een AVG-probleem.
              </p>
              <p>
                De meeste bedrijven lossen dit op door <strong>niks te doen</strong> ("we vertrouwen onze mensen")
                of door het <strong>hele apparaat over te nemen</strong> via MDM. Maar dat wil niemand — je medewerker
                wil niet dat de baas meekijkt op zijn privételefoon.
              </p>
              <p className="font-semibold text-foreground">
                Er is een derde optie. En die is een stuk slimmer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* De oplossing */}
      <section className="py-16 md:py-24 bg-rinqel-green/5">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-rinqel-green/10">
              <ShieldCheck className="h-8 w-8 text-rinqel-green" />
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">
              Beveilig de apps, niet het apparaat
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                In plaats van het hele apparaat over te nemen, beveiligen we <strong>alleen de zakelijke apps</strong>.
                Outlook, Teams, OneDrive — die krijgen een extra beveiligingslaag. Maar de privéfoto's, WhatsApp
                en Instagram van je medewerker? Daar komen we niet aan.
              </p>
              <p>
                Je medewerker merkt er bijna niks van. Misschien een extra pincode bij het openen van Outlook.
                Dat is het. Maar achter de schermen is alles beveiligd: data kan niet gekopieerd worden naar
                privéapps, en bij verlies of vertrek wissen we op afstand alleen de zakelijke data.
              </p>
              <p>
                Het apparaat zelf? Daar verandert <strong>helemaal niks</strong> aan.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Voor en na vergelijking */}
      <section className="border-y border-border bg-muted/30 py-16 md:py-24">
        <div className="container">
          <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl">
            Het verschil
          </h2>
          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
            {/* Zonder */}
            <div className="rounded-2xl border border-destructive/20 bg-card p-8">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                  <XCircle className="h-5 w-5 text-destructive" />
                </div>
                <h3 className="text-xl font-bold text-destructive">Zonder Rinqel</h3>
              </div>
              <ul className="space-y-4">
                {comparison.without.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive/60" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Met */}
            <div className="rounded-2xl border border-rinqel-green/30 bg-card p-8 shadow-lg shadow-rinqel-green/5">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rinqel-green/10">
                  <CheckCircle2 className="h-5 w-5 text-rinqel-green" />
                </div>
                <h3 className="text-xl font-bold text-rinqel-green">Met Rinqel</h3>
              </div>
              <ul className="space-y-4">
                {comparison.with.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section className="py-16 md:py-24">
        <div className="container">
          <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl">
            Wat levert het je op?
          </h2>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {voordelen.map((voordeel) => (
              <div
                key={voordeel.title}
                className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-rinqel-blue/30 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-rinqel-blue/10">
                  <voordeel.icon className="h-6 w-6 text-rinqel-blue" />
                </div>
                <h3 className="mb-2 text-lg font-bold">{voordeel.title}</h3>
                <p className="text-sm text-muted-foreground">{voordeel.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Extra uitleg */}
      <section className="py-12 md:py-16">
        <div className="container">
          <div className="mx-auto max-w-3xl space-y-6 text-muted-foreground">
            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-foreground">Wat we precies doen</h2>
              <p>
                We koppelen de zakelijke apps op de privételefoon aan je Microsoft 365-omgeving en
                zetten daar een beveiligingsbeleid op. De medewerker logt één keer in met zijn
                werkaccount; vanaf dat moment zijn Outlook, Teams en OneDrive afgeschermd met een
                eigen pincode of vingerafdruk.
              </p>
              <p>
                Daarna stellen we de regels in: data uit zakelijke apps kan niet naar privéapps
                gekopieerd worden, screenshots in Outlook blokkeren we, en bij verlies of vertrek
                wissen we op afstand alléén de werkdata. Aan het toestel zelf raken we niet.
              </p>
            </div>

            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-foreground">Wat het oplevert</h2>
              <p>
                Je medewerkers houden hun eigen telefoon en hun privacy, jij houdt grip op de
                bedrijfsdata. Geen tweede zakelijke telefoon, geen MDM-discussies, geen ruzie over
                wie wat mag zien. Wel een AVG-compliant situatie die je aan je accountant en je
                klanten kunt uitleggen.
              </p>
              <p>
                In de praktijk hebben we dit binnen één dag staan, voor zowel iOS als Android.
                Daarna heb je er als ondernemer geen omkijken meer naar — wij beheren de regels,
                jij doet je werk.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-gradient-to-br from-primary/5 via-rinqel-blue/5 to-rinqel-green/5 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Benieuwd wat dit voor jouw bedrijf betekent?
            </h2>
            <p className="mb-8 text-lg text-muted-foreground">
              We vertellen je graag meer. Geen verkooppraatje, gewoon een eerlijk gesprek over hoe je
              bedrijfsdata kunt beveiligen zonder je medewerkers te frustreren.
            </p>
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Button variant="hero" size="lg" asChild>
                <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="praktijk_byod_cta">
                  Bereken je prijs
                </TrackedLink>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="praktijk_byod_cta">
                  Neem contact op
                </TrackedLink>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PraktijkBYOD;
